import type { ComposedForm } from 'lib-node-question-builder';
import { and, formIs, or } from 'lib-node-question-builder/utils';
import { useState } from 'react';

import type { DtoV1Borrower1003BorrowerSummaryAddress } from '~/__generated__/api';
import ConfirmationDialog from '~/common/components/ConfirmationDialog';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import {
  AddFieldArrayRowButton,
  DeleteFieldArrayRowButton,
} from '~/pages/Loan1003DataGrids/common/components/FormFieldArrayButtons';
import { READONLY_CONDITION } from '~/pages/Loan1003DataGrids/common/utils/utils';

import type {
  AddressInfoData,
  BorrowerAddressData,
  BorrowerDropdownOptions,
} from '../../../types';
import {
  AddressCard,
  AddressCardActions,
  AddressCardContent,
  AddressCardSeparator,
  AddressCardTitle,
} from './AddressCard';
import { FieldArrayContainer } from './FieldArrayContainer';
import { PrimaryAddressSelector } from './PrimaryAddressSelector';
import { createCommonAddressTemplate } from './commonAddressTemplate';

type ResidencyHistoryProps = {
  form: ComposedForm<AddressInfoData['formData']>;
  dropdownOptions: Omit<BorrowerDropdownOptions, 'joinedBorrowers'>;
  isPrimaryAddressSelectionEnabled?: boolean;
  primaryAddressSelectionOptions?: DtoV1Borrower1003BorrowerSummaryAddress[];
  initialPrimaryId?: number | null;
  onAddressDeleted?: (addressId: number | null) => void;
};

export function ResidencyHistory({
  form,
  dropdownOptions,
  isPrimaryAddressSelectionEnabled,
  primaryAddressSelectionOptions,
  initialPrimaryId,
  onAddressDeleted,
}: Readonly<ResidencyHistoryProps>) {
  const isWriteAccessDisabled = useIsWriteAccessDisabled();
  const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(
    null,
  );

  return (
    <>
      {isPrimaryAddressSelectionEnabled && (
        <PrimaryAddressSelector
          form={form}
          dropdownOptions={dropdownOptions}
          primaryAddressSelectionOptions={primaryAddressSelectionOptions ?? []}
          initialPrimaryId={initialPrimaryId}
        />
      )}
      <form.Field
        name="residenceHistoryDetails.borrowerResidenceHistory"
        mode="array"
      >
        {(field) => {
          return (
            <FieldArrayContainer>
              {confirmDeleteIndex === null ? null : (
                <ConfirmationDialog
                  openFromExternalMethod
                  title="Remove Primary Address"
                  message="Are you sure you want to remove this primary address?"
                  itemName="Primary Address"
                  onClick={() => {
                    const deletedAddress = (
                      field.state.value as BorrowerAddressData[]
                    )[confirmDeleteIndex];

                    onAddressDeleted?.(
                      typeof deletedAddress?.addressIdentifier?.addressId ===
                        'number'
                        ? deletedAddress.addressIdentifier.addressId
                        : null,
                    );
                    field.removeValue(confirmDeleteIndex);
                    setConfirmDeleteIndex(null);
                  }}
                  onClose={() => {
                    setConfirmDeleteIndex(null);
                  }}
                />
              )}
              {(field.state.value as BorrowerAddressData[])?.map(
                (values, index) => (
                  <AddressCard key={values.addressIdentifier.addressId}>
                    <AddressCardContent width="60%">
                      <AddressCardTitle>Address</AddressCardTitle>
                      <form.Grid
                        name="addressGrid"
                        columns={4}
                        fields={[
                          ...createCommonAddressTemplate(
                            `residenceHistoryDetails.borrowerResidenceHistory[${index}]`,
                          ),
                          {
                            elementType: 'question',
                            type: 'radio',
                            label: 'Primary Residence',
                            showLabel: false,
                            colStart: 1,
                            span: 4,
                            name: `residenceHistoryDetails.borrowerResidenceHistory[${index}].borrowerAddress.primaryResidence`,
                            conditions: {
                              isDisabled: READONLY_CONDITION,
                            },
                            options: [
                              { label: 'Primary', value: 'true' },
                              { label: 'Previous', value: 'false' },
                            ],
                          },
                        ]}
                      />
                    </AddressCardContent>
                    <AddressCardSeparator />
                    <AddressCardContent width="40%">
                      <AddressCardTitle>Residence Info</AddressCardTitle>
                      <form.Grid name="residenceLengthGrid" columns={4}>
                        <form.GridItem>
                          <form.Masked
                            name={`residenceHistoryDetails.borrowerResidenceHistory[${index}].yearsAtResidence`}
                            label="Years"
                            placeholder="Years"
                            pattern="##"
                            rules={{
                              required: true,
                            }}
                            conditions={{
                              isDisabled: READONLY_CONDITION,
                            }}
                          />
                        </form.GridItem>
                        <form.GridItem>
                          <form.Masked
                            name={`residenceHistoryDetails.borrowerResidenceHistory[${index}].monthsAtResidence`}
                            label="Months"
                            placeholder="Months"
                            pattern="##"
                            rules={{
                              required: true,
                              onBlur: {
                                type: 'number',
                                gte: 0,
                                lte: 11,
                                message: 'Must be between 0 and 11',
                              },
                            }}
                            conditions={{
                              isDisabled: READONLY_CONDITION,
                            }}
                          />
                        </form.GridItem>
                        <form.GridItem colSpan={4}>
                          <form.Radio
                            name={`residenceHistoryDetails.borrowerResidenceHistory[${index}].ownershipStatus`}
                            label="Ownership Status"
                            showLabel={false}
                            options={dropdownOptions?.ownershipStatus ?? []}
                            rules={{
                              required: true,
                            }}
                            conditions={{
                              isDisabled: READONLY_CONDITION,
                            }}
                          />
                        </form.GridItem>
                        <form.GridItem colSpan={2}>
                          <form.Currency
                            name={`residenceHistoryDetails.borrowerResidenceHistory[${index}].rentAmount`}
                            label="Rent Amount"
                            placeholder="Rent Amount"
                            rules={{
                              required: true,
                            }}
                            conditions={{
                              isDisabled: READONLY_CONDITION,
                              isVisible: and(
                                or(
                                  formIs(
                                    `residenceHistoryDetails.borrowerResidenceHistory[${index}].ownershipStatus`,
                                    '1',
                                  ),
                                  formIs(
                                    `residenceHistoryDetails.borrowerResidenceHistory[${index}].ownershipStatus`,
                                    1,
                                  ),
                                ),
                                formIs(
                                  `residenceHistoryDetails.borrowerResidenceHistory[${index}].borrowerAddress.primaryResidence`,
                                  true,
                                ),
                              ),
                            }}
                          />
                        </form.GridItem>
                      </form.Grid>
                    </AddressCardContent>
                    <AddressCardActions>
                      <DeleteFieldArrayRowButton
                        readOnlyMode={isWriteAccessDisabled}
                        onClick={() => {
                          if (values.borrowerAddress?.primaryResidence) {
                            setConfirmDeleteIndex(index);

                            return;
                          }

                          onAddressDeleted?.(
                            typeof values.addressIdentifier?.addressId ===
                              'number'
                              ? values.addressIdentifier.addressId
                              : null,
                          );
                          field.removeValue(index);
                        }}
                      />
                    </AddressCardActions>
                  </AddressCard>
                ),
              )}
              <AddFieldArrayRowButton
                readOnlyMode={isWriteAccessDisabled}
                onClick={() => {
                  field.pushValue({
                    addressIdentifier: {
                      addressId: crypto.randomUUID(),
                    },
                  } as never);
                }}
              >
                Address
              </AddFieldArrayRowButton>
            </FieldArrayContainer>
          );
        }}
      </form.Field>
    </>
  );
}
